import { businessDateTimeToDate, toBusinessDateKey } from "@zapis24/utils";

export type AdminAppointmentStatus =
  | "PENDING"
  | "CONFIRMED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CANCELLED"
  | "NO_SHOW";

export type AppointmentForView = {
  status: AdminAppointmentStatus;
};

export type AppointmentForTimeline = AppointmentForView & {
  startsAt: string | Date;
  endsAt: string | Date;
};

export type AppointmentForActions = AppointmentForTimeline;

export type AppointmentForReturnReminder = {
  endsAt: string | Date;
};

export type AppointmentForSummary = AppointmentForView & {
  actualPrice: number | null;
  service: { priceFrom: number };
};

export type AppointmentStatsForVisibleCount = {
  total: number;
  cancelled?: number;
  noShow?: number;
};

export type ScheduleServiceForSelection = {
  id: string;
  durationMin: number;
  isActive?: boolean;
};

export type AppointmentCardDisclosureInput = {
  appointmentId: string;
  expandedAppointmentIds: ReadonlySet<string>;
  hasAvailableActions: boolean;
  panelOpen: boolean;
};

export type AppointmentCardDisclosureState = {
  isExpanded: boolean;
  showActionButtons: boolean;
  toggleLabel: string;
};

export type MasterVisibleAppointmentStatusMeta = {
  label: string;
  borderClass: string;
  badgeClass: string;
  textClass: string;
};

export const RETURN_REMINDER_DAY_OPTIONS = [
  { days: 14, label: "Через 14 дней" },
  { days: 21, label: "Через 21 день" },
  { days: 30, label: "Через 30 дней" },
] as const;

const MASTER_VISIBLE_STATUS_META: Record<
  Exclude<AdminAppointmentStatus, "PENDING" | "CONFIRMED">,
  MasterVisibleAppointmentStatusMeta
> = {
  IN_PROGRESS: {
    label: "В работе",
    borderClass: "border-l-purple-400",
    badgeClass: "bg-purple-50 text-purple-700",
    textClass: "text-purple-700",
  },
  COMPLETED: {
    label: "Завершена",
    borderClass: "border-l-blue-400",
    badgeClass: "bg-blue-50 text-blue-700",
    textClass: "text-blue-700",
  },
  CANCELLED: {
    label: "Отменена",
    borderClass: "border-l-gray-300",
    badgeClass: "bg-gray-100 text-gray-500",
    textClass: "text-gray-500",
  },
  NO_SHOW: {
    label: "Не пришёл",
    borderClass: "border-l-red-400",
    badgeClass: "bg-red-50 text-red-600",
    textClass: "text-red-600",
  },
};

export function getMasterVisibleAppointmentStatusMeta(
  status: AdminAppointmentStatus,
): MasterVisibleAppointmentStatusMeta | null {
  if (status === "PENDING" || status === "CONFIRMED") return null;
  return MASTER_VISIBLE_STATUS_META[status];
}

export function isHiddenAppointmentStatus(status: AdminAppointmentStatus): boolean {
  return status === "CANCELLED" || status === "NO_SHOW";
}

export function getVisibleAppointments<T extends AppointmentForView>(appointments: T[]): T[] {
  return appointments.filter((appointment) => !isHiddenAppointmentStatus(appointment.status));
}

export function getHiddenAppointments<T extends AppointmentForView>(appointments: T[]): T[] {
  return appointments.filter((appointment) => isHiddenAppointmentStatus(appointment.status));
}

export function getAppointmentDaySummary(appointments: AppointmentForSummary[]) {
  const visibleAppointments = getVisibleAppointments(appointments);
  const hiddenAppointments = getHiddenAppointments(appointments);
  const completedAppointments = visibleAppointments.filter(
    (appointment) => appointment.status === "COMPLETED",
  );

  return {
    visibleCount: visibleAppointments.length,
    hiddenCount: hiddenAppointments.length,
    inProgressCount: visibleAppointments.filter(
      (appointment) => appointment.status === "IN_PROGRESS",
    ).length,
    activeCount: visibleAppointments.filter(
      (appointment) => appointment.status === "PENDING" || appointment.status === "CONFIRMED",
    ).length,
    completedCount: completedAppointments.length,
    revenueKopecks: completedAppointments.reduce(
      (sum, appointment) => sum + (appointment.actualPrice ?? appointment.service.priceFrom),
      0,
    ),
  };
}

export function getAppointmentTimelineGroups<T extends AppointmentForTimeline>(
  appointments: T[],
  now = new Date(),
): { past: T[]; current: T[]; upcoming: T[] } {
  const groups: { past: T[]; current: T[]; upcoming: T[] } = {
    past: [],
    current: [],
    upcoming: [],
  };

  const visibleAppointments = getVisibleAppointments(appointments).slice().sort((a, b) => {
    return readDate(a.startsAt).getTime() - readDate(b.startsAt).getTime();
  });

  for (const appointment of visibleAppointments) {
    const startsAt = readDate(appointment.startsAt);
    const endsAt = readDate(appointment.endsAt);

    if (appointment.status === "COMPLETED" || endsAt <= now) {
      groups.past.push(appointment);
    } else if (startsAt <= now && endsAt > now) {
      groups.current.push(appointment);
    } else {
      groups.upcoming.push(appointment);
    }
  }

  return groups;
}

export function getAppointmentActionAvailability(appointment: AppointmentForActions) {
  const isActive =
    appointment.status === "PENDING" ||
    appointment.status === "CONFIRMED" ||
    appointment.status === "IN_PROGRESS";
  const isCompleted = appointment.status === "COMPLETED";
  const isHidden = isHiddenAppointmentStatus(appointment.status);

  return {
    canComplete: isActive,
    canMarkNoShow: isActive,
    canReschedule: isActive,
    canCancel: isActive,
    canContact: isActive,
    canSetReturnReminder: isCompleted,
    canCreateRepeat: isCompleted || isHidden,
    canEditActualPrice: isCompleted,
  };
}

export function getAppointmentCardDisclosureState({
  appointmentId,
  expandedAppointmentIds,
  hasAvailableActions,
  panelOpen,
}: AppointmentCardDisclosureInput): AppointmentCardDisclosureState {
  const isExpanded = panelOpen || expandedAppointmentIds.has(appointmentId);

  return {
    isExpanded,
    showActionButtons: hasAvailableActions && isExpanded && !panelOpen,
    toggleLabel: isExpanded ? "Скрыть действия" : "Показать действия",
  };
}

export function getReturnReminderDateValue(
  appointment: AppointmentForReturnReminder,
  daysAfterVisit: number,
  timeZone?: string,
): string {
  return addDaysToDateKey(toBusinessDateKey(appointment.endsAt, timeZone), daysAfterVisit);
}

export function getReturnReminderAtIso(
  appointment: AppointmentForReturnReminder,
  daysAfterVisit: number,
  timeZone?: string,
): string {
  const dateKey = getReturnReminderDateValue(appointment, daysAfterVisit, timeZone);
  return businessDateTimeToDate(dateKey, "10:00", timeZone).toISOString();
}

export function parseReturnReminderDays(value: string): number | null {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

export function buildCompleteAppointmentPatch({
  appointment,
  actualPriceRubles,
  returnReminderDays,
  timeZone,
}: {
  appointment: AppointmentForReturnReminder;
  actualPriceRubles: string;
  returnReminderDays: string;
  timeZone?: string;
}): { status: "COMPLETED"; actualPrice: number; returnReminderAt?: string } {
  const actualPrice = Math.round(Number.parseFloat(actualPriceRubles || "0") * 100);
  const days = parseReturnReminderDays(returnReminderDays);
  const body: { status: "COMPLETED"; actualPrice: number; returnReminderAt?: string } = {
    status: "COMPLETED",
    actualPrice,
  };

  if (days !== null) {
    body.returnReminderAt = getReturnReminderAtIso(appointment, days, timeZone);
  }

  return body;
}

export function getVisibleAppointmentCountFromStats(
  stats: AppointmentStatsForVisibleCount | undefined,
): number {
  if (!stats) return 0;
  return Math.max(stats.total - (stats.cancelled ?? 0) - (stats.noShow ?? 0), 0);
}

export function selectScheduleServiceId({
  currentServiceId,
  services,
}: {
  currentServiceId: string;
  services: ScheduleServiceForSelection[];
}): string {
  const activeServices = services.filter((service) => service.isActive !== false);
  if (activeServices.length === 0) return "";
  if (activeServices.some((service) => service.id === currentServiceId)) return currentServiceId;

  return activeServices.reduce((shortest, service) =>
    service.durationMin < shortest.durationMin ? service : shortest,
  ).id;
}

function readDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

function addDaysToDateKey(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return [
    String(date.getUTCFullYear()).padStart(4, "0"),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
}
